/**
 * CLE-2811 — the pure half of the generation-progress panel: which phase is active, how far along the
 * overall bar is, what each document is doing, how long each finished phase took, and which of the
 * things the AI looks for it has mentioned so far.
 *
 * Everything here reads real data. The worker reports its phase, per-document pages and (from progress
 * v2) the AI's own words; the only estimate is WITHIN a phase that has no natural count — waiting in the
 * queue, the AI's modeling, saving — where the bar eases towards the end of that phase's band against a
 * stated expectation and never reaches it until the phase really ends.
 *
 * Every v2 field (`documents`, `completedPhases`, `narration`, `activity`, `turns`) is optional: a worker
 * that only sends v1 must still produce a sensible panel, so each helper has a v1 path.
 */

import {
  DraftRunStage,
  DraftRunStatus,
  IWorkerProgress,
  WorkerDocumentStatus,
} from "store/queries/contractModelDrafts";

import {
  FACTS_CHECKLIST_LABELS,
  PHASE_LABELS,
  PROGRESS_TIPS,
  RATES_CHECKLIST_LABELS,
} from "../copy";
import { REUSED_EXTRACTION_DETAIL } from "../utils";

// ---------------------------------------------------------------------------------------------
// Phases
// ---------------------------------------------------------------------------------------------

/** The phase timeline. `saving` covers the worker's `depositing` and the run's `worker_returned`. */
export type TimelinePhase =
  | "queued"
  | "downloading"
  | "extracting"
  | "thinking"
  | "saving";

export const TIMELINE_PHASES: { id: TimelinePhase; label: string }[] = [
  { id: "queued", label: PHASE_LABELS.queued },
  { id: "downloading", label: PHASE_LABELS.downloading },
  { id: "extracting", label: PHASE_LABELS.extracting },
  { id: "thinking", label: PHASE_LABELS.thinking },
  { id: "saving", label: PHASE_LABELS.saving },
];

export const phaseIndex = (phase: TimelinePhase): number =>
  TIMELINE_PHASES.findIndex((p) => p.id === phase);

/**
 * The active phase, or null when the run is not generating. A `running` run with no report yet (the first
 * second after the claim) is downloading: that is the first thing the worker does.
 */
export const activePhaseOf = (
  status: DraftRunStatus,
  progress: IWorkerProgress | null
): TimelinePhase | null => {
  if (status === "queued") return "queued";
  if (status === "worker_returned") return "saving";
  if (status !== "running") return null;
  switch (progress?.phase) {
    case "extracting":
      return "extracting";
    case "thinking":
      return "thinking";
    case "depositing":
      return "saving";
    default:
      return "downloading";
  }
};

// ---------------------------------------------------------------------------------------------
// Small readers — the API is lenient and the worker is a separate deploy, so trust no shape.
// ---------------------------------------------------------------------------------------------

const num = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

const parseMs = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
};

const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));

/** When the worker says the current phase started; for `thinking`, when the AI started. */
export const phaseStartMs = (progress: IWorkerProgress | null): number | null =>
  progress
    ? parseMs(
        (progress.phase === "thinking" ? progress.thinkingStartedAt : null) ??
          progress.phaseStartedAt
      )
    : null;

/** True when the worker reused the previous pass's text instead of downloading and reading again. */
export const isReusePass = (progress: IWorkerProgress | null): boolean =>
  !!progress &&
  (progress.detail === REUSED_EXTRACTION_DETAIL ||
    (Array.isArray(progress.documents) &&
      progress.documents.length > 0 &&
      progress.documents.every((d) => d?.status === "reused")));

// ---------------------------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------------------------

export interface IDocumentRow {
  id: string;
  fileName: string;
  /** Page count; null for a workbook, or before the worker opens it. */
  pages: number | null;
  done: number;
  ocrPages: number;
  status: WorkerDocumentStatus;
}

const DOCUMENT_STATUSES: WorkerDocumentStatus[] = [
  "pending",
  "downloading",
  "extracting",
  "done",
  "reused",
];

/**
 * One row per run document. v2 progress carries each document's state by id; v1 knows only the file in
 * hand (`detail`, with `current`/`total` pages while reading), so the rest show as pending — and once the
 * AI is modeling, every document has been read, which is true on either version.
 */
export const mergeDocuments = (
  runDocuments: { id: string; fileName: string }[],
  status: DraftRunStatus,
  progress: IWorkerProgress | null
): IDocumentRow[] => {
  const blank = (d: { id: string; fileName: string }): IDocumentRow => ({
    id: d.id,
    fileName: d.fileName,
    pages: null,
    done: 0,
    ocrPages: 0,
    status: "pending",
  });
  const phase = activePhaseOf(status, progress);
  const pastReading = phase === "thinking" || phase === "saving";

  // v2: the worker's word per document, by id.
  if (progress && Array.isArray(progress.documents)) {
    const byId = new Map(
      progress.documents
        .filter((d) => d && typeof d.documentId === "string")
        .map((d) => [d.documentId, d])
    );
    const rows = runDocuments.map((d): IDocumentRow => {
      const w = byId.get(d.id);
      if (!w) return pastReading ? { ...blank(d), status: "done" } : blank(d);
      byId.delete(d.id);
      const pages = num(w.pages);
      return {
        id: d.id,
        fileName: d.fileName,
        pages: pages != null && pages > 0 ? pages : null,
        done: Math.max(0, num(w.done) ?? 0),
        ocrPages: Math.max(0, num(w.ocrPages) ?? 0),
        status: DOCUMENT_STATUSES.includes(w.status) ? w.status : "pending",
      };
    });
    // A document the worker reports that the run no longer lists — show it rather than lose it.
    byId.forEach((w) => {
      const pages = num(w.pages);
      rows.push({
        id: w.documentId,
        fileName: w.fileName ?? w.documentId,
        pages: pages != null && pages > 0 ? pages : null,
        done: Math.max(0, num(w.done) ?? 0),
        ocrPages: Math.max(0, num(w.ocrPages) ?? 0),
        status: DOCUMENT_STATUSES.includes(w.status) ? w.status : "pending",
      });
    });
    return rows;
  }

  // v1, or no report yet.
  if (status === "worker_returned" || pastReading)
    return runDocuments.map((d) => ({ ...blank(d), status: "done" }));
  if (!progress) return runDocuments.map(blank);
  if (isReusePass(progress))
    return runDocuments.map((d) => ({ ...blank(d), status: "reused" }));

  let matched = false;
  return runDocuments.map((d) => {
    if (matched || !progress.detail || d.fileName !== progress.detail)
      return blank(d);
    matched = true;
    if (progress.phase === "downloading")
      return { ...blank(d), status: "downloading" };
    const total = num(progress.total);
    return {
      ...blank(d),
      status: "extracting",
      pages: total != null && total > 0 ? total : null,
      done: Math.max(0, num(progress.current) ?? 0),
      ocrPages: Math.max(0, num(progress.ocrPages) ?? 0),
    };
  });
};

/** How far one document is, 0–1; null when there is no count to show (downloading, a workbook). */
export const documentFraction = (row: IDocumentRow): number | null => {
  if (row.status === "done" || row.status === "reused") return 1;
  if (row.status === "pending") return 0;
  if (row.status === "extracting" && row.pages)
    return clamp01(row.done / row.pages);
  return null;
};

/**
 * How much of the reading is done, 0–1. Pages across documents when every document's page count is
 * known; otherwise documents done, each counted with its own page fraction while it is being read.
 */
export const extractionFraction = (rows: IDocumentRow[]): number => {
  if (rows.length === 0) return 0;
  const finished = (r: IDocumentRow) =>
    r.status === "done" || r.status === "reused";
  if (rows.every((r) => r.pages != null && r.pages > 0)) {
    const total = rows.reduce((s, r) => s + (r.pages as number), 0);
    const done = rows.reduce(
      (s, r) =>
        s +
        (finished(r)
          ? (r.pages as number)
          : Math.min(r.done, r.pages as number)),
      0
    );
    return clamp01(done / total);
  }
  const sum = rows.reduce(
    (s, r) => s + (finished(r) ? 1 : documentFraction(r) ?? 0),
    0
  );
  return clamp01(sum / rows.length);
};

// ---------------------------------------------------------------------------------------------
// The overall bar
// ---------------------------------------------------------------------------------------------

/** Each phase's share of the bar, in percent. */
export const PHASE_BANDS: Record<TimelinePhase, [number, number]> = {
  queued: [0, 5],
  downloading: [5, 12],
  extracting: [12, 40],
  thinking: [40, 92],
  saving: [92, 98],
};

/** The worker drains the queue every few seconds; "it usually picks up within 10 seconds". */
export const QUEUE_EXPECTED_MS = 10_000;
/** Without a typical duration, the AI's modeling is expected to take about this long. */
export const DEFAULT_THINKING_EXPECTED_MS = 90_000;
/** The modeling's share of a typical run, when a typical duration is known. */
export const THINKING_SHARE_OF_TYPICAL = 0.7;
/** Depositing is a few writes; clear-api's persist cron runs every 15 seconds. */
export const DEPOSIT_EXPECTED_MS = 5_000;
export const PERSIST_EXPECTED_MS = 15_000;

/** How far into its band a phase with no count is: this share at the expected time, then a slow tail. */
const EASE_SHARE_AT_EXPECTED = 0.85;
const EASE_CEILING = 0.99;

/**
 * 0 → 0.85 linearly over `expectedMs`, then an exponential tail towards 1 with a matching slope, capped
 * below 1: the bar moves at a steady pace through a typical run and slows — never stops, never finishes —
 * past it. Only the phase's real end moves the bar into the next band.
 */
export const easeTowards = (elapsedMs: number, expectedMs: number): number => {
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 0 || expectedMs <= 0)
    return 0;
  const r = elapsedMs / expectedMs;
  const f =
    r <= 1
      ? EASE_SHARE_AT_EXPECTED * r
      : 1 -
        (1 - EASE_SHARE_AT_EXPECTED) *
          Math.exp(
            (-(r - 1) * EASE_SHARE_AT_EXPECTED) / (1 - EASE_SHARE_AT_EXPECTED)
          );
  return Math.min(EASE_CEILING, f);
};

/** How long the AI's modeling is expected to take. */
export const expectedThinkingMs = (typicalDurationMs: number | null): number =>
  typicalDurationMs != null && typicalDurationMs > 0
    ? typicalDurationMs * THINKING_SHARE_OF_TYPICAL
    : DEFAULT_THINKING_EXPECTED_MS;

export interface IOverallInput {
  status: DraftRunStatus;
  progress: IWorkerProgress | null;
  /** From `mergeDocuments`. */
  documents: IDocumentRow[];
  typicalDurationMs: number | null;
  nowMs: number;
  /** When the run was queued (its `updatedAt` while `queued`). */
  queuedAtMs: number | null;
  /** When this page first saw `worker_returned` — clear-api's persist leaves no timestamp to read. */
  returnedSeenAtMs: number | null;
}

const inBand = (phase: TimelinePhase, fraction: number): number => {
  const [lo, hi] = PHASE_BANDS[phase];
  return lo + (hi - lo) * clamp01(fraction);
};

/**
 * The overall percent, 0–98, before the monotonic guard. Queued 0–5 (eased over the queue's expected
 * wait), downloading 5–12 (documents downloaded), reading 12–40 (pages or documents read), modeling 40–92
 * (eased over the expected modeling time), saving 92–98 (depositing, then clear-api's persist). It never
 * reaches 100: the panel disappears when the run is done.
 */
export const overallPercent = (input: IOverallInput): number => {
  const { status, progress, nowMs } = input;
  const phase = activePhaseOf(status, progress);
  if (!phase) return 0;
  switch (phase) {
    case "queued":
      return inBand(
        "queued",
        input.queuedAtMs == null
          ? 0
          : easeTowards(nowMs - input.queuedAtMs, QUEUE_EXPECTED_MS)
      );
    case "downloading": {
      if (!progress) return PHASE_BANDS.downloading[0];
      const current = num(progress.current);
      const total = num(progress.total);
      const byCount =
        current != null && total != null && total > 0
          ? (current - 1) / total
          : 0;
      const byRows = input.documents.length
        ? input.documents.filter(
            (d) =>
              d.status === "extracting" ||
              d.status === "done" ||
              d.status === "reused"
          ).length / input.documents.length
        : 0;
      return inBand("downloading", Math.max(byCount, byRows));
    }
    case "extracting":
      return inBand(
        "extracting",
        isReusePass(progress) ? 1 : extractionFraction(input.documents)
      );
    case "thinking": {
      const started = phaseStartMs(progress);
      return inBand(
        "thinking",
        started == null
          ? 0
          : easeTowards(
              nowMs - started,
              expectedThinkingMs(input.typicalDurationMs)
            )
      );
    }
    case "saving":
    default: {
      if (status === "worker_returned") {
        // The second half of the band: the worker is done, clear-api is persisting.
        const since = input.returnedSeenAtMs;
        return inBand(
          "saving",
          0.5 +
            0.5 *
              (since == null
                ? 0
                : easeTowards(nowMs - since, PERSIST_EXPECTED_MS))
        );
      }
      const started = phaseStartMs(progress);
      return inBand(
        "saving",
        0.5 *
          (started == null
            ? 0
            : easeTowards(nowMs - started, DEPOSIT_EXPECTED_MS))
      );
    }
  }
};

/** The bar's value, remembered across renders so it never goes backwards within one pass. */
export interface IMonotonicPercent {
  key: string;
  value: number;
}

/**
 * Identifies one pass. Queued and claimed are different keys, so a reaper re-queue (a new attempt) and a
 * chained regeneration (a new version) both start the bar again instead of holding the last pass's value.
 */
export const passKeyOf = (run: {
  status: DraftRunStatus;
  startedAt: string | null;
  versionNo: number;
}): string =>
  `${run.versionNo}:${
    run.status === "queued" ? "queued" : run.startedAt ?? "unclaimed"
  }`;

/** The monotonic guard: within the same pass, never lower than what was already shown. */
export const holdMonotonic = (
  prev: IMonotonicPercent | null,
  key: string,
  value: number
): IMonotonicPercent =>
  prev && prev.key === key
    ? { key, value: Math.max(prev.value, value) }
    : { key, value };

/** Whole percent for the label. Floored, so a band's ceiling is shown only once the phase is over. */
export const percentLabel = (value: number): string =>
  `${Math.floor(Math.min(100, Math.max(0, value)))}%`;

// ---------------------------------------------------------------------------------------------
// Phase durations
// ---------------------------------------------------------------------------------------------

/** What this page has seen of the current pass, across polls. */
export interface IPhaseObservations {
  /** When each phase started, as the worker reported it (the latest report wins, for a retried attempt). */
  starts: Partial<Record<TimelinePhase, number>>;
  /** The enqueue time, when this page saw the run queued. */
  queuedAtMs: number | null;
  reused: boolean;
}

export const EMPTY_OBSERVATIONS: IPhaseObservations = {
  starts: {},
  queuedAtMs: null,
  reused: false,
};

/** Fold one poll into what has been seen. Pure: returns the same object when nothing changed. */
export const observeProgress = (
  prev: IPhaseObservations,
  run: {
    status: DraftRunStatus;
    progress: IWorkerProgress | null;
    updatedAt: string;
  }
): IPhaseObservations => {
  let next = prev;
  if (run.status === "queued") {
    const at = parseMs(run.updatedAt);
    if (at != null && at !== prev.queuedAtMs)
      next = { ...next, queuedAtMs: at };
  }
  const phase = activePhaseOf(run.status, run.progress);
  const start = phaseStartMs(run.progress);
  if (run.status === "running" && phase && start != null) {
    if (next.starts[phase] !== start)
      next = { ...next, starts: { ...next.starts, [phase]: start } };
  }
  if (!next.reused && isReusePass(run.progress))
    next = { ...next, reused: true };
  return next;
};

const span = (from: number | null | undefined, to: number | null | undefined) =>
  from != null && to != null && to >= from ? to - from : null;

/**
 * How long each FINISHED phase took, in ms (null when unknown or not finished). The worker reports
 * downloading and reading (v2 `completedPhases`); the rest are read off the boundaries this page saw:
 * queued = enqueue → the worker's claim (`startedAt`), a phase = its start → the next phase's start.
 * Downloading starts at the claim, so it needs no observation of its own.
 */
export const derivePhaseDurations = (input: {
  active: TimelinePhase;
  observations: IPhaseObservations;
  progress: IWorkerProgress | null;
  startedAt: string | null;
  /** The run's `updatedAt` — the enqueue time while the worker holds the run, as its writes don't touch it. */
  updatedAt: string;
}): Record<TimelinePhase, number | null> => {
  const { active, observations, progress } = input;
  const activeAt = phaseIndex(active);
  const startedAtMs = parseMs(input.startedAt);
  const updatedAtMs = parseMs(input.updatedAt);
  const { starts } = observations;
  const completed = progress?.completedPhases ?? null;
  const reported = (v: unknown) => {
    const n = num(v);
    return n != null && n >= 0 ? n : null;
  };

  const queuedAt =
    observations.queuedAtMs ??
    (active !== "queued" &&
    updatedAtMs != null &&
    startedAtMs != null &&
    updatedAtMs <= startedAtMs
      ? updatedAtMs
      : null);

  const durations: Record<TimelinePhase, number | null> = {
    queued: span(queuedAt, startedAtMs),
    downloading:
      reported(completed?.downloading) ??
      (observations.reused
        ? null
        : span(starts.downloading ?? startedAtMs, starts.extracting)),
    extracting:
      reported(completed?.extracting) ??
      span(starts.extracting, starts.thinking),
    thinking: span(starts.thinking, starts.saving),
    saving: null,
  };
  TIMELINE_PHASES.forEach((p, i) => {
    if (i >= activeAt) durations[p.id] = null;
  });
  return durations;
};

/** When the active phase started, for its live counter; null when unknown. */
export const activePhaseStartMs = (input: {
  active: TimelinePhase;
  observations: IPhaseObservations;
  progress: IWorkerProgress | null;
  startedAt: string | null;
  returnedSeenAtMs: number | null;
}): number | null => {
  switch (input.active) {
    case "queued":
      return input.observations.queuedAtMs;
    case "saving":
      return (
        (input.progress?.phase === "depositing"
          ? phaseStartMs(input.progress)
          : null) ??
        input.observations.starts.saving ??
        input.returnedSeenAtMs
      );
    default:
      return (
        phaseStartMs(input.progress) ??
        input.observations.starts[input.active] ??
        (input.active === "downloading" ? parseMs(input.startedAt) : null)
      );
  }
};

// ---------------------------------------------------------------------------------------------
// "What it is looking for"
// ---------------------------------------------------------------------------------------------

export interface IChecklistItem {
  id: string;
  label: string;
  pattern: RegExp;
}

export const FACTS_CHECKLIST: IChecklistItem[] = [
  {
    id: "signed",
    label: FACTS_CHECKLIST_LABELS.signed,
    pattern: /signed|signature/i,
  },
  {
    id: "effective",
    label: FACTS_CHECKLIST_LABELS.effective,
    pattern: /effective/i,
  },
  {
    id: "timelyFiling",
    label: FACTS_CHECKLIST_LABELS.timelyFiling,
    pattern: /timely/i,
  },
  {
    id: "escalators",
    label: FACTS_CHECKLIST_LABELS.escalators,
    pattern: /escalat|increase/i,
  },
  {
    id: "implants",
    label: FACTS_CHECKLIST_LABELS.implants,
    pattern: /implant/i,
  },
  {
    id: "multipleProcedure",
    label: FACTS_CHECKLIST_LABELS.multipleProcedure,
    pattern: /multiple[\s-]+procedure|mppr|mpdr|100\s*\/\s*50/i,
  },
  {
    id: "reimbursement",
    label: FACTS_CHECKLIST_LABELS.reimbursement,
    pattern: /grouper|carve|reimburs|fee[\s-]+schedule/i,
  },
];

export const RATES_CHECKLIST: IChecklistItem[] = [
  {
    id: "baseRateFiles",
    label: RATES_CHECKLIST_LABELS.baseRateFiles,
    pattern: /base[\s-]+rate|rate[\s-]+file|fee[\s-]+schedule/i,
  },
  {
    id: "rateLines",
    label: RATES_CHECKLIST_LABELS.rateLines,
    pattern:
      /service[\s-]+line|rate[\s-]+line|rate[\s-]+calc|outpatient|inpatient|per[\s-]+diem|\bdrg\b|\bapc\b/i,
  },
  {
    id: "carveOuts",
    label: RATES_CHECKLIST_LABELS.carveOuts,
    pattern: /carve/i,
  },
  {
    id: "adjustmentRules",
    label: RATES_CHECKLIST_LABELS.adjustmentRules,
    pattern:
      /adjustment|multiple[\s-]+procedure|mppr|mpdr|case[\s-]+rate|stop[\s-]*loss|outlier|escalat/i,
  },
];

export const checklistFor = (stage: DraftRunStage): IChecklistItem[] =>
  stage === "facts" ? FACTS_CHECKLIST : RATES_CHECKLIST;

/** The ids of the items `text` mentions, in checklist order. */
export const matchChecklist = (
  items: IChecklistItem[],
  text: string | null | undefined
): string[] =>
  text ? items.filter((i) => i.pattern.test(text)).map((i) => i.id) : [];

/**
 * The ticks after this poll: everything already ticked plus whatever the narration mentions now. Never
 * un-ticks — the narration is a sliding window of the last few thousand characters, so a mention scrolls
 * out of it long before the thing stops being found.
 */
export const accumulateTicks = (
  prev: string[],
  items: IChecklistItem[],
  text: string | null | undefined
): string[] => {
  const hit = new Set([...prev, ...matchChecklist(items, text)]);
  const next = items.filter((i) => hit.has(i.id)).map((i) => i.id);
  return next.length === prev.length && next.every((id, i) => id === prev[i])
    ? prev
    : next;
};

// ---------------------------------------------------------------------------------------------
// "Live from the AI"
// ---------------------------------------------------------------------------------------------

/** The AI's narration, or "" when there is none (v1, or not yet). */
export const narrationOf = (progress: IWorkerProgress | null): string =>
  typeof progress?.narration === "string" ? progress.narration : "";

export const MAX_ACTIVITY = 8;

/** The last actions, oldest first. */
export const activityOf = (progress: IWorkerProgress | null): string[] => {
  const list = progress?.activity;
  if (!Array.isArray(list)) return [];
  return list
    .filter((a): a is string => typeof a === "string" && a.trim() !== "")
    .slice(-MAX_ACTIVITY);
};

/** The AI's turn count, or null. */
export const turnsOf = (progress: IWorkerProgress | null): number | null => {
  const n = num(progress?.turns);
  return n != null && n > 0 ? Math.floor(n) : null;
};

export type ActivityKind = "read" | "search" | "write" | "other";

const LEADING_VERB: { kind: Exclude<ActivityKind, "other">; re: RegExp }[] = [
  { kind: "write", re: /^(wr[io]te|writ|edit|sav|creat|updat|record)/i },
  {
    kind: "search",
    re: /^(search|grep|find|found|look|quer|list|glob|ran|run|check)/i,
  },
  { kind: "read", re: /^(read|open|view|load|fetch|scan|skim)/i },
];

const ANYWHERE: { kind: Exclude<ActivityKind, "other">; re: RegExp }[] = [
  { kind: "read", re: /\bread/i },
  { kind: "search", re: /\b(search|grep|query|find)/i },
  { kind: "write", re: /\b(wr[io]te|edit|save)/i },
];

/** Read, search or write, by the action's leading verb (else the first such word anywhere). */
export const activityKind = (text: string): ActivityKind => {
  const first = text.trim().split(/\s+/)[0] ?? "";
  const lead = LEADING_VERB.find((v) => v.re.test(first));
  if (lead) return lead.kind;
  return ANYWHERE.find((v) => v.re.test(text))?.kind ?? "other";
};

// ---------------------------------------------------------------------------------------------
// Tips
// ---------------------------------------------------------------------------------------------

export const TIP_INTERVAL_MS = 12_000;

/** The tip on screen at `nowMs`: a new one every 12 seconds. */
export const tipAt = (nowMs: number, tips: string[] = PROGRESS_TIPS): string =>
  tips.length
    ? tips[Math.floor(Math.max(0, nowMs) / TIP_INTERVAL_MS) % tips.length]
    : "";

// ---------------------------------------------------------------------------------------------
// The last progress of a pass
// ---------------------------------------------------------------------------------------------

/** The progress a page keeps for the pass in flight. */
export interface IRememberedProgress {
  versionNo: number | null;
  progress: IWorkerProgress | null;
}

export const NO_REMEMBERED_PROGRESS: IRememberedProgress = {
  versionNo: null,
  progress: null,
};

/**
 * The worker clears its progress when it deposits, so for the seconds of `worker_returned` the run
 * carries none — and the AI's words, the documents read and the partial preview would vanish just before
 * the real result arrives. Keep the last report of the pass through `worker_returned`; forget it when the
 * run stops generating, is queued again, or a new version starts, so one pass's report never shows under
 * the next. Pure: returns `prev` when nothing changed.
 */
export const rememberProgress = (
  prev: IRememberedProgress,
  run: {
    status: DraftRunStatus;
    versionNo: number;
    progress: IWorkerProgress | null;
  }
): IRememberedProgress => {
  if (run.status !== "running" && run.status !== "worker_returned")
    return prev.progress || prev.versionNo !== null
      ? NO_REMEMBERED_PROGRESS
      : prev;
  if (run.progress)
    return prev.progress === run.progress && prev.versionNo === run.versionNo
      ? prev
      : { versionNo: run.versionNo, progress: run.progress };
  if (run.status === "worker_returned" && prev.versionNo === run.versionNo)
    return prev;
  return prev.progress === null && prev.versionNo === run.versionNo
    ? prev
    : { versionNo: run.versionNo, progress: null };
};

/**
 * The strip's short form of a typical duration: "~45 s" under a minute, else minutes to the nearest half —
 * "~1 min", "~1½ min", "~12 min". Null when there is none.
 */
export const compactDuration = (ms: number | null): string | null => {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return null;
  const seconds = Math.max(1, Math.round(ms / 1000));
  if (seconds < 60) return `~${seconds} s`;
  const halves = Math.round(ms / 30_000);
  return `~${Math.floor(halves / 2)}${halves % 2 ? "½" : ""} min`;
};
